import { createRouter, createWebHistory } from "vue-router";
import { getAuth } from "firebase/auth";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      component: () => import("../views/HomeRoute.vue"),
      meta: {
        title: "GCDP Members Home",
        public: true,
      },
    },
    {
      path: "/captain",
      component: () => import("../views/CaptainRoute.vue"),
      meta: {
        title: "GCDP Members Home",
      },
    },
    {
      path: "/sign-in",
      component: () => import("../views/LoginRoute.vue"),
      meta: {
        title: "GCDP Members - Sign In",
      },
    },
    {
      path: "/data",
      component: () => import("../views/DataFileRoute.vue"),
      meta: {
        title: "GCDP Members - Manage Data",
      },
    },
    {
      name: "maps",
      path: "/maps/:state/:county/:precinct?",
      component: () => import("../views/MapsRoute.vue"),
      meta: {
        title: "GCDP Members - Maps",
      },
    },
    {
      name: "precinct",
      path: "/precinct/:state/:county/:precinct?",
      component: () => import("../views/PrecinctRoute.vue"),
      meta: {
        title: "GCDP Members - Precinct Details",
      },
    },
    {
      name: "precinct-sorter",
      path: "/admin/precinct-sorter/:state/:county",
      component: () => import("../views/PrecinctSorterRoute.vue"),
      meta: {
        title: "GCDP Members - Precinct Sorter",
      },
    },
    {
      name: "role-editor",
      path: "/admin/role-editor",
      component: () => import("../views/RoleEditorView.vue"),
      meta: {
        title: "GCDP Members - Role Editor",
      },
    },
    {
      name: "new-users",
      path: "/admin/new-users",
      component: () => import("../views/NewUsersRoute.vue"),
      meta: {
        title: "GCDP Members - New Users",
      },
    },
    {
      name: "impersonate",
      path: "/admin/impersonate",
      component: () =>
        import("@/common/components/admin/ImpersonationComponent.vue"),
      meta: {
        title: "GCDP Members - Admin Impersonation",
      },
    },
    {
      name: "template-editor",
      path: "/admin/template-editor",
      component: () => import("../views/TemplateEditorRoute.vue"),
      meta: {
        title: "GCDP Members - Template Editor",
      },
    },
    {
      name: "contributions",
      path: "/contributions/:state/:county/:precinct?",
      component: () => import("../views/ContributionsRoute.vue"),
      meta: {
        title: "GCDP Members - Contribution Statistics",
      },
    },
    {
      path: "/contact-contributions/:contactId",
      component: () => import("../views/ContactContributionsRoute.vue"),
      meta: {
        title: "GCDP Members - Contact Contributions",
      },
    },
    {
      path: "/matches",
      component: () => import("../views/MatchRoute.vue"),
      meta: {
        title: "GCDP Members - Member to Voter Matches",
      },
    },
    {
      path: "/privacy",
      meta: {
        public: true,
        title: "GCDP Members - Privacy Policy",
      },
      component: () => import("../views/PrivacyRoute.vue"),
    },
    {
      name: "login",
      path: "/login",
      meta: {
        public: true,
        title: "GCDP Members - Sign In",
      },
      component: () => import("../views/LoginRoute.vue"),
    },
    {
      name: "password-reset",
      path: "/password-reset",
      meta: {
        public: true,
        title: "GCDP Members - Password Reset",
      },
      component: () => import("../views/PasswordResetRoute.vue"),
    },
    {
      name: "register",
      path: "/register",
      meta: {
        public: true,
        title: "GCDP Members - Register",
      },
      component: () => import("../views/RegisterRoute.vue"),
    },
    {
      name: "membership",
      path: "/membership/:state/:county/:precinct?",
      component: () => import("@/views/MembershipRoute.vue"),
      meta: {
        title: "GCDP Members - Membership",
      },
    },
    {
      name: "member-profile",
      path: "/member-profile",
      component: () => import("../views/MemberProfileRoute.vue"),
      meta: {
        title: "GCDP Members - Member Profile",
      },
    },
    {
      name: "user-profile",
      path: "/user-profile",
      component: () =>
        import("@/common/components/profile/UserProfileComponent.vue"),
      meta: {
        title: "GCDP Members - User Profile",
      },
    },
    {
      name: "members",
      path: "/members",
      component: () => import("../views/MembersRoute.vue"),
      meta: {
        title: "GCDP Members - Members",
      },
    },
    {
      name: "captains",
      path: "/captains",
      component: () => import("../views/PublicCaptainsRoute.vue"),
      meta: {
        public: true,
        title: "GCDP - Captains",
      },
    },
    {
      name: "docs",
      path: "/docs",
      component: () => import("../views/DocumentationRoute.vue"),
      meta: {
        public: false,
        title: "GCDP - Documentation",
      },
    },
    {
      path: "/tasks",
      name: "TaskManager",
      component: () => import("../views/TaskManagerView.vue"),
      meta: {
        requiresAuth: true,
        title: "Task Manager",
      },
    },
    {
      name: "volunteer-interest-admin",
      path: "/admin/volunteer-interests",
      component: () => import("../views/VolunteerInterestAdminRoute.vue"),
      meta: {
        title: "GCDP Members - Volunteer Interest Editor",
      },
    },
    {
      name: "mail-browser",
      path: "/admin/mail-browser",
      component: () => import("../views/MailBrowserRoute.vue"),
      meta: {
        requiresAuth: true,
        title: "GCDP Members - Mail Browser",
      },
    },
    {
      name: "volunteer-search",
      path: "/volunteers",
      component: () => import("../views/VolunteerSearchRoute.vue"),
      meta: {
        requiresAuth: true,
        title: "GCDP Members - Volunteer Search",
      },
    },
  ],
});

router.beforeEach((to) => {
  document.title = to.meta?.title ?? "Default Title";
});

router.beforeEach((to, from, next) => {
  // Check if gtag is available (it's added to window by VueGtag)
  if (typeof window !== "undefined" && window.gtag) {
    const auth = getAuth();
    const user = auth.currentUser;

    // Track page views with custom data
    window.gtag("config", "G-PX5FKD9PFT", {
      page_title: to.meta.title || to.name,
      page_location: window.location.href,
      page_path: to.path,
      custom_map: {
        custom_dimension_1: "route_name",
        custom_dimension_2: "user_authenticated",
        custom_dimension_3: "route_category",
      },
    });

    // Send page view event with additional context
    window.gtag("event", "page_view", {
      page_title: to.meta.title || to.name,
      page_location: window.location.href,
      page_path: to.path,
      route_name: to.name || "unnamed",
      user_authenticated: user ? "true" : "false",
      route_category: getRouteCategory(to.path),
      is_public_route: to.meta.public ? "true" : "false",
    });

    // Track navigation patterns
    if (from.name) {
      window.gtag("event", "navigation", {
        event_category: "user_flow",
        from_route: from.name,
        to_route: to.name,
        navigation_type: from.path === "/" ? "from_home" : "internal",
      });
    }
  }

  next();
});

// Helper function to categorize routes
function getRouteCategory(path) {
  if (path.startsWith("/admin")) return "admin";
  if (path.startsWith("/maps")) return "maps";
  if (path.startsWith("/data")) return "data";
  if (path.startsWith("/member")) return "membership";
  if (path.includes("precinct")) return "precinct";
  if (path.includes("contribution")) return "contributions";
  if (["/login", "/register", "/password-reset"].includes(path)) return "auth";
  if (path === "/") return "home";
  return "other";
}

export default router;
